import React, { Component, Fragment } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import {
  compose,
  descend,
  prop,
  sortWith,
  groupWith,
  defaultTo,
  filter,
  find,
  propEq,
  isEmpty,
  sum,
  head,
  includes,
  without,
} from 'ramda';
import { connect } from 'react-redux';
import moment from 'moment';

import magic from '../../../../../../util/magic';
import { isNilOrEmpty } from '../../../../../../util/redux';
import { apiSendExperienceValidation } from '../../../../../../api/experience';
import registrationActions from '../../../../../../redux/registration';

import File from '../../../../../../components/File/index';
import ExperiencesList from '../../../../../../components/ExperiencesList';
import { handleSuccessBase, handleErrorBase } from '../../../../../../util/forms';
import ViewValidationModal from '../../../../../../components/Modal/ViewValidationModal';
import Checkbox from '../../../../../../components/Forms/Checkbox';

const mapDispatch = {
  ...registrationActions,
};
class FinalSummaryTable extends Component {
  static propTypes = {
    admin: PropTypes.bool,
    registration: PropTypes.object,
    user: PropTypes.object,
    updateRegistration: PropTypes.func,
  };

  state = {
    showValidationModal: false,
    validationToShow: {},
    experiencesToValidate: [],
  };

  renderCertificateName = () => {
    const { registration } = this.props;
    if (isNilOrEmpty(registration.certificateName)) return null;

    return (
      <tr>
        <th>Certificate Name</th>
        <td>{registration.certificateName}</td>
      </tr>
    );
  };

  renderResume = (files) => {
    const { registration } = this.props;
    const resumeFiles = filter(propEq('type', magic.resume), files);
    if (isNilOrEmpty(registration.educationUniversity)) return null;

    return (
      <tr>
        <th>Resume</th>
        <td>
          <div>
            <strong>University:</strong> {registration.educationUniversity}
          </div>
          <div>
            <strong>City:</strong> {registration.educationCity}
          </div>
          <div>
            <strong>State:</strong> {registration.educationState}
          </div>
          <div>
            <strong>Graduation Year:</strong> {registration.educationGraduationYear}
          </div>
          <div>
            <strong>Degree:</strong> {registration.educationDegree}
          </div>
          <div>
            <strong>Other Certifications:</strong> {registration.educationOtherCertifications}
          </div>
          {resumeFiles.length > 0 && (
            <div>
              <strong>Detailed Resume:</strong>{' '}
              {resumeFiles.map((file) => (
                <File key={file.id} file={file} />
              ))}
            </div>
          )}
        </td>
      </tr>
    );
  };

  renderExperience = () => {
    const { registration, admin } = this.props;
    const registrationExperiences = defaultTo([], registration.experiences);
    if (isEmpty(registrationExperiences)) return null;

    return (
      <div className="my-5">
        <h4>
          Experience Hours (Approved Hours: {sum(registrationExperiences.map((e) => (e.approved ? e.hours : 0)))})
        </h4>
        <ExperiencesList admin={admin} registration={registration} experiences={registrationExperiences} />
      </div>
    );
  };

  renderExperienceValidation = () => {
    const { registration, updateRegistration, updateRegistrationValidations } = this.props;
    const { experiencesToValidate } = this.state;
    const groupedExperiences = compose(
      groupWith((a, b) => a.supervisorEmail == b.supervisorEmail),
      sortWith([descend(prop('supervisorEmail'))]),
    )(registration.experiences);
    const experienceValidation = defaultTo([], registration.experienceValidation);
    const isValidating = registration.status == magic.experienceApproval;
    if (isEmpty(registration.experiences)) return null;

    return (
      <div className="my-5">
        <h4>
          Experience Hours Supervisor Validation (Validated Hours: {sum(experienceValidation.map((e) => e.hours))})
        </h4>
        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th />
                <th>Email</th>
                <th>Hours</th>
                <th>Signature</th>
                <th>Email Sent</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {groupedExperiences.map((experiences) => {
                const experience = head(experiences);
                const validation = compose(
                  defaultTo({}),
                  find(propEq('supervisorEmail', experience.supervisorEmail)),
                )(experienceValidation);
                const sentOn = isEmpty(validation) ? null : moment(validation.updatedAt).format('MM/DD/YYYY');
                const checked = includes(experience.id, experiencesToValidate);
                const toggleSending = (e) => {
                  e.preventDefault();
                  this.setState({
                    experiencesToValidate: checked
                      ? without([experience.id], experiencesToValidate)
                      : [...experiencesToValidate, experience.id],
                  });
                };

                return (
                  <tr key={experience.id} onClick={toggleSending} className={cn({ clickableRow: isValidating })}>
                    <td>
                      {isValidating && <Checkbox name={`experiencesToValidate[${experience.id}]`} checked={checked} />}
                    </td>
                    <td>{experience.supervisorEmail}</td>
                    <td>{validation.hours}</td>
                    <td>{validation.signature}</td>
                    <td>{sentOn ? `Sent on ${sentOn}` : 'Not sent'}</td>
                    <td>
                      {!isNilOrEmpty(validation.signature) && (
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => this.setState({ showValidationModal: true, validationToShow: validation })}
                        >
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {isValidating && (
          <button
            className="btn btn-primary float-right"
            disabled={isEmpty(experiencesToValidate)}
            onClick={() =>
              apiSendExperienceValidation({ experiences: experiencesToValidate })
                .then(({ data }) => updateRegistrationValidations(data))
                .then(() => handleSuccessBase())
                .catch((e) => handleErrorBase())
            }
          >
            Send Validations
          </button>
        )}
      </div>
    );
  };

  renderEmploymentVerification = (files) => {
    const clientLetterFiles = filter(propEq('type', magic.clientLetter), files);
    const peerLetterFiles = filter(propEq('type', magic.peerLetter), files);
    const supervisorLetterFiles = filter(propEq('type', magic.supervisorLetter), files);
    const employmentVerificationFiles = filter(propEq('type', magic.employmentVerification), files);
    if (
      isEmpty(clientLetterFiles) &&
      isEmpty(peerLetterFiles) &&
      isEmpty(supervisorLetterFiles) &&
      isEmpty(employmentVerificationFiles)
    )
      return null;

    return (
      <tr>
        <th>Employment Verification</th>
        <td>
          {clientLetterFiles.length > 0 && (
            <div>
              <strong>Client Letter: </strong>
              {clientLetterFiles.map((file) => (
                <File key={file.id} file={file} />
              ))}
            </div>
          )}
          {peerLetterFiles.length > 0 && (
            <div>
              <strong>Peer Letter: </strong>
              {peerLetterFiles.map((file) => (
                <File key={file.id} file={file} />
              ))}
            </div>
          )}
          {supervisorLetterFiles.length > 0 && (
            <div>
              <strong>Supervisor Letter: </strong>
              {supervisorLetterFiles.map((file) => (
                <File key={file.id} file={file} />
              ))}
            </div>
          )}
          {employmentVerificationFiles.length > 0 && (
            <div>
              <strong>Employment Verification: </strong>
              {employmentVerificationFiles.map((file) => (
                <File key={file.id} file={file} />
              ))}
            </div>
          )}
        </td>
      </tr>
    );
  };

  renderPreferences = () => {
    const { registration, user } = this.props;
    if (
      magic.postRegistrationSteps.indexOf(registration.step) <
      magic.postRegistrationSteps.indexOf(magic.preferencesStep)
    ) {
      return null;
    }

    return (
      <tr>
        <th>Preferences</th>
        <td>
          <div>
            <strong>Do you provide counseling services?</strong> {user.provideCounselingServices ? 'Yes' : 'No'}
          </div>
          <div>
            <strong>Do you offer virtual counseling?</strong> {user.offerVirtualCounseling ? 'Yes' : 'No'}
          </div>
          <div>
            <strong>Do you offer local counseling?</strong> {user.offerLocalCounseling ? 'Yes' : 'No'}
          </div>
          <div>
            <strong>Do you offer group workshops?</strong> {user.offerGroupWorkshops ? 'Yes' : 'No'}
          </div>
          <div>
            <strong>Areas of Expertise:</strong> {user.areasOfExpertise.map((area) => area.name).join(', ')}
          </div>
        </td>
      </tr>
    );
  };

  renderCodeOfEthics = () => {
    const { registration } = this.props;
    if (isNilOrEmpty(registration.codeOfEthicsName)) return null;

    return (
      <tr>
        <th>Code of Ethics</th>
        <td>
          <div>
            <strong>Name:</strong> {registration.codeOfEthicsName}
          </div>
          <div>
            <strong>Signature:</strong> {registration.codeOfEthicsSignature}
          </div>
          <div>
            <strong>Date:</strong> {registration.codeOfEthicsSignatureDate}
          </div>
        </td>
      </tr>
    );
  };

  render() {
    const { admin, registration } = this.props;
    const { showValidationModal, validationToShow } = this.state;
    const files = defaultTo([], registration.media);

    return (
      <Fragment>
        {this.renderExperience()}
        {admin && this.renderExperienceValidation()}

        {registration.step != magic.examStep && (
          <Fragment>
            <h4>Post Registration Progress</h4>
            <table className="table">
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {this.renderEmploymentVerification(files)}
                {this.renderCertificateName()}
                {this.renderResume(files)}
                {this.renderPreferences()}
                {this.renderCodeOfEthics()}
              </tbody>
            </table>
          </Fragment>
        )}

        <ViewValidationModal
          show={showValidationModal}
          onHide={() => this.setState({ showValidationModal: false, validationToShow: {} })}
          validation={validationToShow}
        />
      </Fragment>
    );
  }
}

export default connect(null, mapDispatch)(FinalSummaryTable);
